import { lazy } from 'react';
import { z } from 'zod';
import { toDataState, useMetricQuery } from '../_sdk/hooks';
import { aggSchema, decimalsSchema, metricBinding, rangeSchema, unitSchema } from '../_sdk/schema';
import { defineWidget } from '../_sdk/types';

/** Compact trend, no axes (PRD VS-042). Plain SVG: no chart library for a polyline. */

export const PropsSchema = z.object({
  ...metricBinding,
  range: rangeSchema.default('24h'),
  agg: aggSchema.default('avg'),
  /** Counters (bytes received…): show the per-second rate. */
  rate: z.boolean().default(false),
  unit: unitSchema,
  decimals: decimalsSchema,
});

export type SparklineProps = z.output<typeof PropsSchema>;
export type SparklineData = [number, number][];

export default defineWidget<SparklineProps, SparklineData>({
  type: 'sparkline',
  category: 'metric',
  defaultSize: { w: 3, h: 2 },
  minSize: { w: 2, h: 2 },
  propsSchema: PropsSchema,
  defaults: { range: '24h', agg: 'avg', rate: false, decimals: 0, tags: {} },
  sourceIds: (p) => [p.sourceId],
  useData: (p) =>
    toDataState(
      useMetricQuery({
        sourceId: p.sourceId,
        name: p.metricName,
        tags: p.tags,
        range: p.range,
        agg: p.agg,
        fn: p.rate ? 'rate' : undefined,
      }),
      {
        select: (r) => r.series[0]?.points ?? [],
        isEmpty: (points) => points.length < 2,
        lastUpdated: (points) => {
          const last = points.at(-1);
          return last ? new Date(last[0]) : undefined;
        },
      },
    ),
  Render: lazy(() => import('./render')),
  Editor: lazy(() => import('../_sdk/metric-editor')),
});
